/**
 * Deal-category display labels — shared client/server (Wave 1).
 *
 * Replaces the hand-mirrored pair: DEAL_CATEGORY_LABELS in
 * src/types/theo-workflow.types.ts ↔ the label map in
 * functions/src/util/deal-service-type.ts. One source of values; the client
 * re-types it against its DealCategory enum.
 *
 * ⚠ SECURITY's label is "Surveillance" — a display-only rename. The stored
 * enum value stays 'SECURITY'; do NOT rename the key without a data
 * migration. When ADDING a category, remember the be-everywhere checklist
 * (KNOWN_ISSUES M-8): every DealCategory switch/map in both packages.
 */

// Literal-keyed (satisfies, not a Record annotation) so the client's typed
// view keeps COMPILE-TIME exhaustiveness: adding a DealCategory enum member
// without a label here fails the client typecheck instead of rendering
// undefined.
export const DEAL_CATEGORY_LABELS = {
  SMART_HOME: 'Smart Home',
  NBN: 'NBN',
  STARLINK: 'Starlink',
  WIFI_EXTENSION: 'WiFi Extension',
  SECURITY: 'Surveillance',
  ACCESS_CONTROL: 'Access Control',
  AV: 'AV',
  IT: 'IT',
  VOIP: 'VoIP',
  FEE_FOR_SERVICE: 'Fee For Service',
} satisfies Record<string, string>;

/**
 * Human service-type label for a deal category (e.g. the
 * `{{deal.serviceType}}` email variable). Unknown / trade-pack categories
 * (open-ended DealCategoryKey) fall back to a title-cased version of the
 * raw value so a pack category like `switchboard-upgrade` still reads as
 * "Switchboard Upgrade". Empty/missing input → ''.
 */
export function dealServiceTypeLabel(category?: string | null): string {
  if (!category || category.trim() === '') return '';
  const known = (DEAL_CATEGORY_LABELS as Record<string, string>)[category.trim()];
  if (known) return known;
  return category
    .trim()
    .replace(/[_-]+/g, ' ')
    .replace(/\s+/g, ' ')
    .replace(/\b\w/g, (c) => c.toUpperCase());
}
